// DepartmentAutocomplete — shared picker департамента (U-20, sled-V9 W6).
// MUI Autocomplete с КЛИЕНТСКОЙ фильтрацией по названию (вариант A ТЗ §5.2):
// данные — useDepartments() (GET /api/departments, уже загружены формами),
// серверный q-поиск не требуется; в DOM показывается максимум MAX_VISIBLE_OPTIONS —
// 1258 «IT Dept %» не раскрываются на весь список.
// Подпись опции — d.name (U-15: никаких ID); value/onChange — id департамента.
// Паттерн повторяет UserAutocomplete (Template Method, GoF §ТЗ): value/onChange/error/helperText.
import { useMemo } from 'react';
import { Autocomplete, TextField, type TextFieldProps } from '@mui/material';
import { useDepartments, type Department } from '@/api/departments';
import { t } from '@/i18n';

/** Максимум опций в выпадающем списке (U-20: при 1258 записях DOM ≤ 50 пунктов). */
const MAX_VISIBLE_OPTIONS = 50;

/**
 * Клиентская фильтрация опций: подстрока по названию (case-insensitive),
 * результат ограничен MAX_VISIBLE_OPTIONS. Пустой ввод — все (до лимита).
 */
function filterDepartments(options: Department[], inputValue: string): Department[] {
  const needle = inputValue.trim().toLowerCase();
  const matched = needle
    ? options.filter((d) => d.name.toLowerCase().includes(needle))
    : options;
  return matched.slice(0, MAX_VISIBLE_OPTIONS);
}

interface DepartmentAutocompleteProps {
  /** Label поля. */
  label: string;
  /** id выбранного департамента ('' — не выбран). */
  value: string;
  /** Колбэк выбора: id департамента или '' при очистке. */
  onChange: (departmentId: string) => void;
  /** Обязательное поле. */
  required?: boolean;
  /** Disabled. */
  disabled?: boolean;
  /** Ошибка валидации формы. */
  error?: boolean;
  /** Текст ошибки/подсказки. */
  helperText?: string | undefined;
  /** ARIA-label для a11y (default = label). */
  'aria-label'?: string | undefined;
}

/**
 * Picker департамента с клиентским поиском по названию (U-20).
 * value/onChange — id департамента; подписи и фильтрация — внутри компонента.
 */
export function DepartmentAutocomplete({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error = false,
  helperText,
  'aria-label': ariaLabel,
}: DepartmentAutocompleteProps): JSX.Element {
  const departments = useDepartments();

  // Выбранный id резолвится в объект (подпись в input даже при фильтрации мимо).
  const selected = useMemo(
    () => departments?.find((d) => d.id === value) ?? null,
    [departments, value],
  );

  return (
    <Autocomplete<Department, false, false, false>
      options={departments ?? []}
      value={selected}
      onChange={(_, option) => onChange(option?.id ?? '')}
      getOptionLabel={(d) => d.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(options, state) => filterDepartments(options, state.inputValue)}
      openOnFocus
      disabled={disabled}
      noOptionsText={t('common.departmentSearchHint')}
      renderInput={(params) => (
        <TextField
          // AutocompleteRenderInputParams несёт явные undefined (size и др.), что под
          // exactOptionalPropertyTypes несовместимо с TextFieldProps — точечный cast.
          {...(params as unknown as TextFieldProps)}
          label={label}
          required={required}
          error={error}
          {...(helperText !== undefined ? { helperText } : {})}
          inputProps={{ ...params.inputProps, 'aria-label': ariaLabel ?? label }}
        />
      )}
    />
  );
}
